import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/axios";
import moment from "moment";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./dashboard.css";

const STATUS_ORDER = ["RECEIVED", "CUTTING", "STITCHING", "READY", "DELIVERED"];
const STATUS_VAR = {
  RECEIVED: "--brass",
  CUTTING: "--warn",
  STITCHING: "--brand",
  READY: "--good",
  DELIVERED: "--ink-mute",
};

const KPI_PAGE_SIZE = 10;

const fmt = (n) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : n >= 1000
    ? `₹${(n / 1000).toFixed(1)}K`
    : `₹${n}`;

export default function Dashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showMoney, setShowMoney] = useState(false);

  const [activeKpi, setActiveKpi] = useState(null);
  const [kpiOrders, setKpiOrders] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiExpanded, setKpiExpanded] = useState(null);
  const [kpiPage, setKpiPage] = useState(0);
  const [kpiTotalPages, setKpiTotalPages] = useState(0);
  const [kpiTotalElements, setKpiTotalElements] = useState(0);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((res) => setData(res.data.body))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const applyStatusChange = (billNo, newStatus) => {
    setData((prev) => ({
      ...prev,
      recentOrders: prev.recentOrders.map((o) =>
        o.billNo === billNo ? { ...o, status: newStatus } : o
      ),
    }));
    setKpiOrders((prev) =>
      prev.map((o) => (o.billNo === billNo ? { ...o, status: newStatus } : o))
    );
  };

  const handleStatusChange = (billNo, newStatus) => {
    api
      .put(`/order/${billNo}/status?status=${newStatus}`)
      .then(() => applyStatusChange(billNo, newStatus));
  };

  const handleRecordPayment = (billNo, amount) => {
    return api.put(`/order/${billNo}/payment?amount=${amount}`).then((res) => {
      const updated = res.data.body;
      setData((prev) => ({
        ...prev,
        recentOrders: prev.recentOrders.map((o) =>
          o.billNo === billNo ? { ...o, advance: updated.advance } : o
        ),
      }));
      setKpiOrders((prev) =>
        prev
          .map((o) => (o.billNo === billNo ? { ...o, advance: updated.advance } : o))
          // an order that just got fully paid drops out of the pending-payments view
          .filter((o) => {
            if (activeKpi !== "pendingPayments" || o.billNo !== billNo) return true;
            const pending = (o.amount || 0) + (o.clothAdvance || 0) - (updated.advance || 0);
            return pending > 0;
          })
      );
    });
  };

  const fetchKpiPage = (kpiKey, page) => {
    setKpiLoading(true);
    api
      .get(`/order/kpi/${kpiKey}?page=${page}&size=${KPI_PAGE_SIZE}`)
      .then((res) => {
        const body = res.data.body;
        setKpiOrders(body.content || []);
        setKpiTotalPages(body.totalPages || 0);
        setKpiTotalElements(body.totalElements || 0);
        setKpiPage(body.currentPage || 0);
      })
      .catch(() => {
        setKpiOrders([]);
        setKpiTotalPages(0);
        setKpiTotalElements(0);
      })
      .finally(() => setKpiLoading(false));
  };

  const handleKpiClick = (kpiKey) => {
    if (activeKpi === kpiKey) {
      setActiveKpi(null);
      setKpiOrders([]);
      setKpiExpanded(null);
      return;
    }
    setActiveKpi(kpiKey);
    setKpiExpanded(null);
    fetchKpiPage(kpiKey, 0);
  };

  const kpiTitles = {
    today: t("dashboard.todayOrders"),
    month: t("dashboard.totalOrders"),
    dueToday: t("dashboard.pendingToday"),
    overdue: t("dashboard.overdue"),
    pendingPayments: t("dashboard.pendingPayments"),
  };

  return (
    <div>
      <Header />
      <div className="tab-container dp-dash">
        {loading || !data ? (
          <div className="dp-dash-loading">
            <div className="dp-spinner" />
          </div>
        ) : (
          <>
            <div className="dp-dash-toolbar">
              <button
                type="button"
                className="dp-money-toggle"
                onClick={() => setShowMoney((v) => !v)}
              >
                {showMoney ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.6 21.6 0 0 1 5.06-6.06M9.9 4.24A10.4 10.4 0 0 1 12 4c7 0 11 8 11 8a21.6 21.6 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <path d="M1 1l22 22" />
                  </svg>
                )}
                {showMoney ? "Hide amounts" : "Show amounts"}
              </button>
            </div>

            {/* ── KPI CARDS ── */}
            <div className="dp-dash-kpis">
              <KpiCard
                active={activeKpi === "today"}
                onClick={() => handleKpiClick("today")}
                label={t("dashboard.todayOrders")}
                value={data.todayOrders}
                icon="📋"
                tone="--brand"
              />
              <KpiCard
                label={t("dashboard.monthRevenue")}
                value={fmt(data.thisMonthRevenue)}
                icon="💰"
                tone="--good"
                money
                showMoney={showMoney}
                growth={{ current: data.thisMonthRevenue, previous: data.lastMonthRevenue }}
              />
              <KpiCard
                active={activeKpi === "dueToday"}
                onClick={() => handleKpiClick("dueToday")}
                label={t("dashboard.pendingToday")}
                value={data.pendingDeliveriesToday}
                icon="🕐"
                tone="--warn"
                alert={data.pendingDeliveriesToday > 0}
              />
              <KpiCard
                active={activeKpi === "overdue"}
                onClick={() => handleKpiClick("overdue")}
                label={t("dashboard.overdue")}
                value={data.overdueOrders}
                icon="⚠️"
                tone="--bad"
                alert={data.overdueOrders > 0}
              />
              <KpiCard
                active={activeKpi === "pendingPayments"}
                onClick={() => handleKpiClick("pendingPayments")}
                label={t("dashboard.pendingPayments")}
                value={fmt(data.pendingPayments)}
                icon="💳"
                tone="--brand"
                money
                showMoney={showMoney}
              />
              <KpiCard
                active={activeKpi === "month"}
                onClick={() => handleKpiClick("month")}
                label={t("dashboard.totalOrders")}
                value={data.monthOrders?.toLocaleString()}
                icon="📦"
                tone="--ink-mute"
                growth={{ current: data.monthOrders, previous: data.lastMonthOrders }}
              />
            </div>

            {/* ── KPI DRILLDOWN ── */}
            {activeKpi && (
              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>
                    {kpiTitles[activeKpi]}
                    {kpiTotalElements > 0 && (
                      <span className="dp-panel-count"> ({kpiTotalElements})</span>
                    )}
                  </h4>
                  <button className="dp-panel-close" onClick={() => handleKpiClick(activeKpi)}>
                    ×
                  </button>
                </div>
                <div className="dp-panel-body dp-recent-body">
                  {kpiLoading ? (
                    <div className="dp-dash-loading dp-kpi-loading">
                      <div className="dp-spinner" />
                    </div>
                  ) : kpiOrders.length === 0 ? (
                    <p className="dp-kpi-empty">{t("common.noData")}</p>
                  ) : (
                    kpiOrders.map((o) => (
                      <OrderRow
                        key={o.billNo}
                        order={o}
                        isOpen={kpiExpanded === o.billNo}
                        onToggle={() =>
                          setKpiExpanded(kpiExpanded === o.billNo ? null : o.billNo)
                        }
                        onStatusChange={handleStatusChange}
                        onRecordPayment={handleRecordPayment}
                        showMoney={showMoney}
                        t={t}
                      />
                    ))
                  )}
                </div>
                {kpiTotalPages > 1 && (
                  <div className="dp-kpi-pagination">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={kpiPage === 0 || kpiLoading}
                      onClick={() => fetchKpiPage(activeKpi, kpiPage - 1)}
                    >
                      ‹ {t("common.prev")}
                    </button>
                    <span>
                      {kpiPage + 1} / {kpiTotalPages}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={kpiPage + 1 >= kpiTotalPages || kpiLoading}
                      onClick={() => fetchKpiPage(activeKpi, kpiPage + 1)}
                    >
                      {t("common.next")} ›
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="dp-dash-row">
              {/* ── STATUS BREAKDOWN ── */}
              <div className="dp-panel dp-dash-status">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.ordersByStatus")}</h4>
                </div>
                <div className="dp-panel-body">
                  <div className="dp-status-list">
                    {STATUS_ORDER.map((s) => {
                      const count = data.ordersByStatus?.[s] || 0;
                      const total = Object.values(data.ordersByStatus || {}).reduce((a, v) => a + v, 0);
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <div key={s} className="dp-status-row">
                          <div className="dp-status-meta">
                            <span className="dp-status-dot" style={{ background: `var(${STATUS_VAR[s]})` }} />
                            <span className="dp-status-name">{t(`status.${s}`)}</span>
                            <span className="dp-status-count">{count.toLocaleString()}</span>
                            <span className="dp-status-pct">{pct}%</span>
                          </div>
                          <div className="dp-status-bar-bg">
                            <div
                              className="dp-status-bar-fill"
                              style={{ width: `${pct}%`, background: `var(${STATUS_VAR[s]})` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── MONTHLY REVENUE ── */}
              <div className="dp-panel dp-dash-revenue">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.monthlyRevenue")}</h4>
                </div>
                <div className="dp-panel-body">
                  <MonthlyBarChart data={data.monthlyRevenue} showMoney={showMoney} />
                </div>
              </div>
            </div>

            {/* ── CHARTS ROW ── */}
            <div className="dp-charts-row">
              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.orderVolume")}</h4>
                </div>
                <div className="dp-panel-body">
                  <OrderVolumeChart data={data.dailyOrders} />
                </div>
              </div>

              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.garmentMix")}</h4>
                </div>
                <div className="dp-panel-body dp-donut-panel">
                  <Donut
                    segments={[
                      { label: t("common.pant"), value: data.pantOrders, colorVar: "--brand" },
                      { label: t("common.shirt"), value: data.shirtOrders, colorVar: "--brass" },
                    ]}
                  />
                </div>
              </div>

              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.deliveryPipeline")}</h4>
                </div>
                <div className="dp-panel-body dp-donut-panel">
                  <Donut
                    segments={[
                      { label: "On track", value: data.upcomingDeliveries, colorVar: "--good" },
                      { label: t("dashboard.pendingToday"), value: data.pendingDeliveriesToday, colorVar: "--warn" },
                      { label: t("dashboard.overdue"), value: data.overdueOrders, colorVar: "--bad" },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* ── TOP CUSTOMERS + WORKER WORKLOAD ── */}
            <div className="dp-dash-row">
              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.topCustomers")}</h4>
                </div>
                <div className="dp-panel-body dp-topcust-body">
                  {(data.topCustomers || []).length === 0 ? (
                    <p className="dp-kpi-empty">{t("common.noData")}</p>
                  ) : (
                    data.topCustomers.map((c, i) => (
                      <div key={c.mobile} className="dp-topcust-row">
                        <div className={`dp-topcust-rank${i < 3 ? " top" : ""}`}>{i + 1}</div>
                        <div className="dp-topcust-info">
                          <div className="dp-topcust-name">{c.name}</div>
                          <div className="dp-topcust-meta">Last order {c.lastOrderDate}</div>
                        </div>
                        <div className="dp-topcust-stats">
                          <div className="dp-topcust-spend">
                            <Money hide={!showMoney}>₹{c.totalSpend.toLocaleString()}</Money>
                          </div>
                          <div className="dp-topcust-count">{c.orderCount} orders</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="dp-panel">
                <div className="dp-panel-head">
                  <span className="dp-panel-dot" />
                  <h4>{t("dashboard.workerWorkload")}</h4>
                </div>
                <div className="dp-panel-body">
                  {(data.workerOutput || []).length === 0 ? (
                    <p className="dp-kpi-empty">{t("common.noData")}</p>
                  ) : (
                    (() => {
                      const maxPieces = Math.max(...data.workerOutput.map((w) => w.pieces), 1);
                      return data.workerOutput.map((w) => {
                        const pct = Math.round((w.pieces / maxPieces) * 100);
                        const barColor = pct >= 70 ? "--good" : pct >= 35 ? "--warn" : "--bad";
                        return (
                          <div key={w.workerId} className="dp-worker-row">
                            <div className="dp-worker-meta">
                              <span className="dp-worker-name">{w.name}</span>
                              <span className="dp-worker-pieces">{w.pieces} pieces this month</span>
                            </div>
                            <div className="dp-worker-bar-bg">
                              <div
                                className="dp-worker-bar-fill"
                                style={{ width: `${pct}%`, background: `var(${barColor})` }}
                              />
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                </div>
              </div>
            </div>

            {/* ── ACTIVITY FEED ── */}
            <div className="dp-panel">
              <div className="dp-panel-head">
                <span className="dp-panel-dot" />
                <h4>{t("dashboard.activityFeed")}</h4>
              </div>
              <div className="dp-panel-body dp-activity-body">
                {(data.recentActivity || []).length === 0 ? (
                  <p className="dp-kpi-empty">{t("common.noData")}</p>
                ) : (
                  data.recentActivity.map((a, i) => (
                    <div key={a.id} className="dp-activity-row">
                      <div className="dp-activity-rail">
                        <span className={`dp-activity-dot ${a.type}`} />
                        {i < data.recentActivity.length - 1 && <span className="dp-activity-line" />}
                      </div>
                      <div className="dp-activity-content">
                        <div className="dp-activity-desc">
                          {a.billNo && <span className="dp-activity-bill">{a.billNo}</span>}{" "}
                          {a.description}
                        </div>
                        <div className="dp-activity-time">{moment(a.createdAt).fromNow()}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── RECENT ORDERS (expandable rows) ── */}
            <div className="dp-panel">
              <div className="dp-panel-head">
                <span className="dp-panel-dot" />
                <h4>{t("dashboard.recentOrders")}</h4>
              </div>
              <div className="dp-panel-body dp-recent-body">
                {(data.recentOrders || []).map((o) => (
                  <OrderRow
                    key={o.billNo}
                    order={o}
                    isOpen={expanded === o.billNo}
                    onToggle={() => setExpanded(expanded === o.billNo ? null : o.billNo)}
                    onStatusChange={handleStatusChange}
                    onRecordPayment={handleRecordPayment}
                    showMoney={showMoney}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Money({ children, hide }) {
  return hide ? <span className="dp-money-blur">{children}</span> : children;
}

function GrowthBadge({ current, previous }) {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return null;
  const up = pct > 0;
  return (
    <div className={`dp-growth-badge${up ? " up" : " down"}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        {up ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M12 5v14M5 12l7 7 7-7" />}
      </svg>
      {up ? "+" : ""}{pct}%
    </div>
  );
}

function KpiCard({ label, value, icon, tone, alert, onClick, active, money, showMoney, growth }) {
  return (
    <button
      type="button"
      className={`dp-kpi${alert ? " dp-kpi-alert" : ""}${active ? " dp-kpi-active" : ""}${onClick ? " dp-kpi-clickable" : ""}`}
      style={{ "--tone": `var(${tone})` }}
      onClick={onClick}
      disabled={!onClick}
    >
      <div className="dp-kpi-bar" />
      {growth && <GrowthBadge current={growth.current} previous={growth.previous} />}
      <div className="dp-kpi-icon">{icon}</div>
      <div className="dp-kpi-body">
        <div className="dp-kpi-value">
          <Money hide={money && !showMoney}>{value}</Money>
        </div>
        <div className="dp-kpi-label">{label}</div>
      </div>
    </button>
  );
}

function MonthlyBarChart({ data, showMoney }) {
  const rows = data || [];
  const maxRevenue = Math.max(...rows.map((m) => m.revenue), 1);
  return (
    <div className="dp-bar-chart">
      {rows.map((m, i) => (
        <div key={i} className="dp-bar-col">
          <span className="dp-bar-val">
            {m.revenue > 0 && <Money hide={!showMoney}>{fmt(m.revenue)}</Money>}
          </span>
          <div className="dp-bar-wrap">
            <div className="dp-bar" style={{ height: `${Math.round((m.revenue / maxRevenue) * 100)}%` }} />
          </div>
          <span className="dp-bar-label">{m.month.split(" ")[0]}</span>
        </div>
      ))}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="dp-chart-tooltip">
      {label && <div className="dp-chart-tooltip-label">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="dp-chart-tooltip-row">
          <span className="dp-chart-tooltip-dot" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.name}: <strong>{p.value?.toLocaleString?.() ?? p.value}</strong></span>
        </div>
      ))}
    </div>
  );
}

function OrderVolumeChart({ data }) {
  const rows = data || [];
  if (rows.length === 0) return null;
  const labelEvery = Math.ceil(rows.length / 6);

  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={rows} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="dpVolumeFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.28} />
            <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="var(--line)" vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: "var(--ink-mute)" }}
          interval={labelEvery - 1}
          axisLine={{ stroke: "var(--line)" }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "var(--ink-mute)" }}
          axisLine={false}
          tickLine={false}
          width={26}
          allowDecimals={false}
        />
        <Tooltip content={<ChartTooltip />} />
        <Area
          type="monotone"
          dataKey="count"
          name="Orders"
          stroke="var(--brand)"
          strokeWidth={2}
          fill="url(#dpVolumeFill)"
          dot={{ r: 2.5, fill: "var(--brand)", strokeWidth: 0 }}
          activeDot={{ r: 4.5 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function Donut({ segments }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  const chartData = segments.map((s) => ({ name: s.label, value: s.value || 0, colorVar: s.colorVar }));

  return (
    <div className="dp-donut-wrap">
      <div className="dp-donut-chart">
        <ResponsiveContainer width={108} height={108}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              innerRadius={34}
              outerRadius={54}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={`var(${entry.colorVar})`} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="dp-donut-center-label">
          <span className="dp-donut-total">{total.toLocaleString()}</span>
        </div>
      </div>
      <div className="dp-donut-legend">
        {segments.map((s, i) => {
          const pct = total > 0 ? Math.round(((s.value || 0) / total) * 100) : 0;
          return (
            <div key={i} className="dp-legend-row">
              <span className="dp-status-dot" style={{ background: `var(${s.colorVar})` }} />
              <span className="dp-legend-label">{s.label}</span>
              <span className="dp-legend-value">{(s.value || 0).toLocaleString()}</span>
              <span className="dp-legend-pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderRow({ order: o, isOpen, onToggle, onStatusChange, onRecordPayment, showMoney, t }) {
  const [paymentInput, setPaymentInput] = useState("");
  const [saving, setSaving] = useState(false);

  const pending = (o.amount || 0) + (o.clothAdvance || 0) - (o.advance || 0);

  const submitPayment = () => {
    const amt = parseInt(paymentInput, 10);
    if (!amt || amt <= 0) return;
    setSaving(true);
    onRecordPayment(o.billNo, amt).finally(() => {
      setSaving(false);
      setPaymentInput("");
    });
  };

  return (
    <div className={`dp-recent-row${isOpen ? " open" : ""}`}>
      <button type="button" className="dp-recent-summary" onClick={onToggle}>
        <svg className="dp-recent-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m9 6 6 6-6 6" />
        </svg>
        <span className="dp-recent-bill">{o.billNo}</span>
        <span className="dp-recent-name">{o.customer?.name}</span>
        <span className="dp-recent-date">
          {o.deliveryDate ? moment(o.deliveryDate).format("DD/MM/YYYY") : "—"}
        </span>
        <span className="dp-recent-amt">
          <Money hide={!showMoney}>₹{(o.amount || 0) + (o.clothAdvance || 0)}</Money>
        </span>
        <StatusBadge status={o.status} t={t} />
      </button>

      {isOpen && (
        <div className="dp-recent-detail">
          <div className="dp-recent-detail-grid">
            <div>
              <span className="dp-sub-label">{t("common.mobile")}</span>
              <p>{o.customer?.mobile || "—"}</p>
            </div>
            <div>
              <span className="dp-sub-label">{t("common.address")}</span>
              <p>{o.customer?.address || "—"}</p>
            </div>
            <div>
              <span className="dp-sub-label">{t("common.pant")}</span>
              <p>{o.customer?.pant?.pantCount ?? 0}</p>
            </div>
            <div>
              <span className="dp-sub-label">{t("common.shirt")}</span>
              <p>{o.customer?.shirt?.shirtCount ?? 0}</p>
            </div>
            <div>
              <span className="dp-sub-label">{t("common.date")}</span>
              <p>{o.date ? moment(o.date).format("DD/MM/YYYY") : "—"}</p>
            </div>
            <div>
              <span className="dp-sub-label">{t("common.advance")}</span>
              <p>
                <Money hide={!showMoney}>
                  ₹{o.advance || 0}
                  {pending > 0 && <span className="dp-pending-hint"> · ₹{pending} pending</span>}
                </Money>
              </p>
            </div>
          </div>

          <div className="dp-recent-actions">
            <div className="dp-recent-status-change">
              <span className="dp-sub-label">Update status</span>
              <select
                className="dp-status-select"
                value={o.status || "RECEIVED"}
                onChange={(e) => onStatusChange(o.billNo, e.target.value)}
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{t(`status.${s}`)}</option>
                ))}
              </select>
            </div>

            {pending > 0 && (
              <div className="dp-payment-form">
                <span className="dp-sub-label">{t("dashboard.recordPayment")}</span>
                <div className="dp-payment-input-row">
                  <input
                    type="number"
                    min="1"
                    max={pending}
                    placeholder={t("dashboard.amountReceived")}
                    value={paymentInput}
                    onChange={(e) => setPaymentInput(e.target.value)}
                    className="dp-payment-input"
                  />
                  <button
                    type="button"
                    className="btn btn-sm dp-record-btn"
                    disabled={saving || !paymentInput}
                    onClick={submitPayment}
                  >
                    {t("dashboard.recordBtn")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status, t }) {
  return (
    <span className="dp-status-badge" style={{ "--sc": `var(${STATUS_VAR[status] || "--ink-mute"})` }}>
      {t ? t(`status.${status}`) : status}
    </span>
  );
}
